import { Button, Checkbox, Form, Spin, Switch, message, Popconfirm, Select, Space, Table } from "antd";
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, FilterOutlined, PictureOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { serverFetch } from "../../server";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import SettingsBanner from "./settings-banner";
import "./settings-banners.css";

const extToContentType = (fileName) => {
  const ext = (fileName || "").split(".").pop()?.toLowerCase();
  if (ext === "png") return "image/png";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "gif") return "image/gif";
  if (ext === "webp") return "image/webp";
  if (ext === "bmp") return "image/bmp";
  if (ext === "svg") return "image/svg+xml";
  return "application/octet-stream";
};

const readFileBytes = async (file) => {
  const buffer = await file.arrayBuffer();
  return Array.from(new Uint8Array(buffer));
};

const LS_SELECTED_PROG_KEY = "settingsBanners.selectedProgId";
const LS_ONLY_ACTIVE_KEY = "settingsBanners.onlyActive";

const normalizeBase64 = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("data:")) {
    return trimmed;
  }

  return `data:image/*;base64,${trimmed}`;
};

const SettingsBanners = () => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProgId, setSelectedProgId] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_SELECTED_PROG_KEY);
      if (raw === null || raw === "") return undefined;
      const parsed = Number(raw);
      return Number.isNaN(parsed) ? undefined : parsed;
    } catch {
      return undefined;
    }
  });
  const [onlyActive, setOnlyActive] = useState(() => {
    try {
      return localStorage.getItem(LS_ONLY_ACTIVE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploadFileList, setUploadFileList] = useState([]);
  const [selectedBannerId, setSelectedBannerId] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [selectedImageName, setSelectedImageName] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftSelectedProgId, setDraftSelectedProgId] = useState(undefined);
  const [draftOnlyActive, setDraftOnlyActive] = useState(false);

  const loadPrograms = () => {
    const progCompany = process.env.REACT_APP_PROG_COMPANY;
    const url = progCompany ? `dict/prog?progCompany=${encodeURIComponent(progCompany)}` : "dict/prog";
    serverFetch(url)
      .then((data) => setPrograms(Array.isArray(data) ? data : []))
      .catch(() => setPrograms([]));
  };

  const loadItems = () => {
    setLoading(true);
    const queryParams = selectedProgId != null ? { progId: selectedProgId } : undefined;
    serverFetch("Banner", { queryParams })
      .then((data) => {
        const rows = Array.isArray(data) ? data : [];
        setItems(rows);
        if (selectedBannerId && !rows.some((x) => x.id === selectedBannerId)) {
          setSelectedBannerId(null);
          if (selectedImageUrl) URL.revokeObjectURL(selectedImageUrl);
          setSelectedImageUrl(null);
          setSelectedImageName(null);
        }
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        message.error(e?.userMessage || "Ошибка загрузки баннеров");
      });
  };

  useEffect(() => {
    loadPrograms();
  }, []);

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgId]);

  useEffect(() => {
    try {
      if (selectedProgId == null) {
        localStorage.removeItem(LS_SELECTED_PROG_KEY);
      } else {
        localStorage.setItem(LS_SELECTED_PROG_KEY, String(selectedProgId));
      }
    } catch {
      // noop
    }
  }, [selectedProgId]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_ONLY_ACTIVE_KEY, onlyActive ? "1" : "0");
    } catch {
      // noop
    }
  }, [onlyActive]);

  useEffect(() => {
    return () => {
      if (selectedImageUrl) URL.revokeObjectURL(selectedImageUrl);
    };
  }, [selectedImageUrl]);

  const programNameById = useMemo(() => {
    const map = new Map();
    programs.forEach((x) => map.set(x.id, x.name));
    return map;
  }, [programs]);

  const openAdd = () => {
    setEditingBanner(null);
    setUploadFileList([]);
    form.resetFields();
    form.setFieldsValue({
      progId: selectedProgId ?? programs[0]?.id,
      isAct: true,
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingBanner(record);
    setUploadFileList([]);
    form.setFieldsValue({
      name: record.name,
      progId: record.progId,
      isAct: !!record.isAct,
      komment: record.komment,
      url: record.url,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setUploadFileList([]);
  };

  const handleDelete = (id) => {
    setLoading(true);
    serverFetch(`Banner/${id}`, { method: "DELETE" })
      .then(() => {
        message.success("Баннер удален");
        loadItems();
      })
      .catch((e) => {
        setLoading(false);
        message.error(e?.userMessage || "Ошибка удаления баннера");
      });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const fileObj = uploadFileList[0]?.originFileObj;
      const payload = {
        id: editingBanner?.id ?? 0,
        name: values.name,
        progId: values.progId,
        isAct: !!values.isAct,
        komment: values.komment || null,
        url: values.url || null,
        fileName: fileObj?.name ?? editingBanner?.fileName ?? null,
      };

      if (fileObj) {
        payload.img = await readFileBytes(fileObj);
      } else if (editingBanner) {
        payload.img = null;
      }

      setLoading(true);
      if (editingBanner) {
        await serverFetch("Banner", { method: "PUT", bodyData: payload });
        message.success("Баннер обновлен");
      } else {
        await serverFetch("Banner", { method: "POST", bodyData: payload });
        message.success("Баннер добавлен");
      }
      closeModal();
      loadItems();
    } catch (e) {
      if (e?.errorFields) return;
      setLoading(false);
      message.error(e?.userMessage || "Ошибка сохранения баннера");
    }
  };

  const showBannerImage = async (record) => {
    setSelectedBannerId(record.id);
    setPreviewLoading(true);
    if (!record.hasImg) {
      if (selectedImageUrl) URL.revokeObjectURL(selectedImageUrl);
      setSelectedImageUrl(null);
      setSelectedImageName(null);
      setPreviewLoading(false);
      return;
    }

    try {
      const full = await serverFetch(`Banner/${record.id}`);
      const img = full?.img;
      if (!img) {
        if (selectedImageUrl) URL.revokeObjectURL(selectedImageUrl);
        setSelectedImageUrl(null);
        setSelectedImageName(null);
        setPreviewLoading(false);
        return;
      }

      if (selectedImageUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(selectedImageUrl);
      }

      if (Array.isArray(img)) {
        if (img.length === 0) {
          setSelectedImageUrl(null);
          setSelectedImageName(null);
          setPreviewLoading(false);
          return;
        }
        const bytes = new Uint8Array(img);
        const type = extToContentType(full?.fileName);
        const blob = new Blob([bytes], { type });
        const objectUrl = URL.createObjectURL(blob);
        setSelectedImageUrl(objectUrl);
      } else {
        const base64Url = normalizeBase64(img);
        if (!base64Url) {
          setSelectedImageUrl(null);
          setSelectedImageName(null);
          return;
        }
        setSelectedImageUrl(base64Url);
      }

      setSelectedImageName(full?.fileName || record.fileName || "banner");
    } catch (e) {
      message.error(e?.userMessage || "Ошибка загрузки картинки");
    } finally {
      setPreviewLoading(false);
    }
  };

  const columns = [
    { dataIndex: "id", title: "#", width: 70 },
    { dataIndex: "name", title: "Название" },
    {
      dataIndex: "progId",
      title: "Программа",
      width: 220,
      render: (id) => programNameById.get(id) ?? id ?? "—",
    },
    {
      dataIndex: "isAct",
      title: "Активен",
      width: 110,
      render: (val) => <Checkbox checked={!!val} disabled />,
    },
    {
      dataIndex: "hasImg",
      title: "Картинка",
      width: 120,
      render: (val) => <Checkbox checked={!!val} disabled />,
    },
    {
      dataIndex: "url",
      title: "URL",
      ellipsis: true,
    },
    {
      title: "",
      width: 110,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="Удалить баннер?" onConfirm={() => handleDelete(record.id)} okText="Да" cancelText="Нет">
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredItems = onlyActive ? items.filter((x) => x.isAct) : items;
  const filterSummary = useMemo(() => {
    const parts = [];
    parts.push(selectedProgId != null ? (programNameById.get(selectedProgId) ?? `Программа #${selectedProgId}`) : "Все программы");
    if (onlyActive) {
      parts.push("Только активные");
    }
    return parts.join(" · ");
  }, [onlyActive, programNameById, selectedProgId]);

  const openFilterModal = () => {
    setDraftSelectedProgId(selectedProgId);
    setDraftOnlyActive(onlyActive);
    setFilterOpen(true);
  };

  const applyFilter = () => {
    setSelectedProgId(draftSelectedProgId);
    setOnlyActive(draftOnlyActive);
    setFilterOpen(false);
  };

  const cancelFilter = () => {
    setFilterOpen(false);
  };

  const handleCardKeyDown = (e, record) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openEdit(record);
    }
  };

  const formModalTitle = editingBanner ? "Редактирование баннера" : "Добавление баннера";

  return (
    <div className="settings-section-root settings-banners">
      <div className="settings-section-toolbar">
        {isMobile ? (
          <div className="settings-banners-toolbar-mobile">
            <div className="company-backups-mobile-filter-row settings-banners-mobile-filter-row">
              <span className="company-backups-mobile-filter-summary" title={filterSummary}>
                {filterSummary}
              </span>
              <Button
                type="default"
                icon={<FilterOutlined />}
                onClick={openFilterModal}
                aria-label="Фильтр"
                className="company-backups-mobile-filter-icon-btn"
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openAdd}
                aria-label="Добавить"
                className="settings-banners-toolbar-icon-btn"
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={loadItems}
                loading={loading}
                aria-label="Обновить"
                className="settings-banners-toolbar-icon-btn"
              />
            </div>
          </div>
        ) : (
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
              Добавить
            </Button>
            <Button icon={<ReloadOutlined />} onClick={loadItems} loading={loading}>
              Обновить
            </Button>
            <Select
              value={selectedProgId}
              allowClear
              style={{ minWidth: 260 }}
              placeholder="Все программы"
              options={programs.map((p) => ({ label: p.name, value: p.id }))}
              onChange={(value) => setSelectedProgId(value)}
            />
            <Checkbox checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)}>
              Только активные
            </Checkbox>
          </Space>
        )}
      </div>
      {isMobile && filterOpen ? (
        <div
          className="misoft-modal-overlay company-backups-filter-overlay--top"
          onClick={(e) => e.target === e.currentTarget && cancelFilter()}
        >
          <div
            className="misoft-modal-content company-backups-filter-sheet--top"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="company-base-sheet-header">
              <span className="company-base-sheet-title">Фильтр</span>
              <Space size="small" className="company-base-sheet-actions">
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={applyFilter}
                  aria-label="Применить"
                  className="company-base-header-icon-btn"
                />
                <Button
                  type="default"
                  icon={<CloseOutlined />}
                  onClick={cancelFilter}
                  aria-label="Отмена"
                  className="company-base-header-icon-btn"
                />
              </Space>
            </div>
            <div className="company-base-sheet-body company-backups-filter-sheet-body">
              <div className="company-backups-filter-field">
                <span className="company-backups-filter-label">Программа</span>
                <Select
                  value={draftSelectedProgId}
                  allowClear
                  style={{ width: "100%" }}
                  placeholder="Все программы"
                  options={programs.map((p) => ({ label: p.name, value: p.id }))}
                  onChange={(value) => setDraftSelectedProgId(value)}
                />
              </div>
              <div className="company-backups-filter-field company-backups-filter-switch-row">
                <span className="company-backups-filter-label">Только активные</span>
                <Checkbox checked={draftOnlyActive} onChange={(e) => setDraftOnlyActive(e.target.checked)} />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="settings-section-scroll">
        {isMobile ? (
          <Spin spinning={loading}>
            <div className="settings-banners-mobile-list">
              {!loading && filteredItems.length === 0 ? (
                <div className="settings-banners-preview-empty">Нет баннеров</div>
              ) : (
                filteredItems.map((record) => (
                  <div
                    key={record.id}
                    className="settings-banners-mobile-card"
                    onClick={() => openEdit(record)}
                    onKeyDown={(e) => handleCardKeyDown(e, record)}
                    role="button"
                    tabIndex={0}
                  >
                    <div
                      className="settings-banners-mobile-card-delete"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <Popconfirm
                        title="Удалить баннер?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Да"
                        cancelText="Нет"
                      >
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label="Удалить" />
                      </Popconfirm>
                    </div>

                    <div className="settings-banners-mobile-row settings-banners-mobile-row-top">
                      <span>
                        <strong>#</strong> {record.id}
                      </span>
                      <span className="settings-banners-mobile-name">{record.name ?? "—"}</span>
                    </div>
                    <div className="settings-banners-mobile-row">
                      <span>{programNameById.get(record.progId) ?? record.progId ?? "—"}</span>
                      <span className="settings-banners-mobile-flags">
                        <span className="settings-banners-mobile-flag">
                          <Switch size="small" checked={!!record.isAct} disabled />
                        </span>
                        <span className="settings-banners-mobile-flag">
                          <PictureOutlined
                            className={record.hasImg ? "settings-banners-mobile-img-icon is-active" : "settings-banners-mobile-img-icon"}
                          />
                        </span>
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Spin>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredItems}
            loading={loading}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
            rowClassName={(record) => (record.id === selectedBannerId ? "settings-banners-row-selected" : "")}
            onRow={(record) => ({
              onClick: () => showBannerImage(record),
              style: { cursor: "pointer" },
            })}
          />
        )}
      </div>

      {!isMobile ? (
        <div className="settings-banners-preview">
          <Spin spinning={previewLoading}>
            {selectedBannerId ? (
              selectedImageUrl ? (
                <>
                  <div className="settings-banners-preview-title">Картинка: {selectedImageName}</div>
                  <img className="settings-banners-preview-image" src={selectedImageUrl} alt={selectedImageName || "banner"} />
                </>
              ) : (
                <div className="settings-banners-preview-empty">У выбранного баннера нет картинки</div>
              )
            ) : (
              <div className="settings-banners-preview-empty">Выберите строку для просмотра картинки</div>
            )}
          </Spin>
        </div>
      ) : null}

      <SettingsBanner
        isMobile={isMobile}
        open={modalOpen}
        title={formModalTitle}
        loading={loading}
        onSave={handleSave}
        onClose={closeModal}
        form={form}
        programs={programs}
        uploadFileList={uploadFileList}
        setUploadFileList={setUploadFileList}
        editingBanner={editingBanner}
      />
    </div>
  );
};

export default SettingsBanners;
